import React, { useEffect, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { trackEvent } from "./analytics";

export default function Contact() {
    const recaptchaRef = useRef(null);
    const recaptchaSiteKey = "6LclZHstAAAAACh1iiklBeD3lpqrb876f7wFgRCA";
    const [formState, setFormState] = useState({
        name: "",
        organisation: "",
        email: "",
        message: "",
        companyWebsite: "",
    });
    const [recaptchaToken, setRecaptchaToken] = useState("");
    const [submitState, setSubmitState] = useState("idle");
    const [formError, setFormError] = useState("");
    const [hasStarted, setHasStarted] = useState(false);

    useEffect(() => {
        document.title = "Contact";
    }, []);

    const onFieldChange = (event) => {
        const { name, value } = event.target;

        if (!hasStarted) {
            setHasStarted(true);
            trackEvent("contact_form_started", {
                page_path: "/contact",
            });
        }

        setFormState((current) => ({
            ...current,
            [name]: value,
        }));
    };

    const onFormSubmit = async (event) => {
        event.preventDefault();
        setSubmitState("submitting");
        setFormError("");

        const { currentTarget } = event;

        if (formState.companyWebsite.trim() !== "") {
            setSubmitState("idle");
            return;
        }

        if (!recaptchaSiteKey) {
            setSubmitState("error");
            setFormError("This contact form is temporarily unavailable. Please email laura@lcmillsconsulting.com directly.");
            return;
        }

        if (!recaptchaToken) {
            setSubmitState("error");
            setFormError("Please complete the reCAPTCHA check before submitting.");
            return;
        }

        const payload = {
            name: formState.name,
            organisation: formState.organisation,
            email: formState.email,
            message: formState.message,
            recaptchaToken,
        };

        try {
            const response = await fetch(currentTarget.action, {
                method: "POST",
                body: JSON.stringify(payload),
                headers: {
                    Accept: "application/json",
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                throw new Error("Request failed");
            }

            setSubmitState("success");
            setFormState({
                name: "",
                organisation: "",
                email: "",
                message: "",
                companyWebsite: "",
            });
            setRecaptchaToken("");
            if (recaptchaRef.current) {
                recaptchaRef.current.reset();
            }
            setHasStarted(false);

            trackEvent("contact_form_submitted", {
                page_path: "/contact",
                status: "success",
            });
        } catch {
            setSubmitState("error");
            setFormError("We could not send your message right now. Please try again, or email laura@lcmillsconsulting.com directly.");

            trackEvent("contact_form_submitted", {
                page_path: "/contact",
                status: "error",
            });
        }
    };

    const showSuccess = submitState === "success";
    const showError = submitState === "error";

    return (
        <div className="contact-page">
            <div className="container">
                <div className="contact-body body">
                    <div className="contact-hero">
                        <p className="contact-kicker">Contact</p>
                        <h2>Start the conversation with confidence</h2>
                        <p className="contact-intro">Whether you need hands-on support with submissions or strategic advice for improving rankings outcomes, I would be pleased to hear more about your goals.</p>
                    </div>

                    <div className="contact-grid row">
                        <div className="col-md-7">
                            <div id="contact" className="contact-form-panel">
                                <h3>{showSuccess ? "Message Sent" : "Send a Message"}</h3>
                                <p className="contact-panel-copy">{showSuccess ? "Thank you for your message. I will get back to you shortly." : "Share a few details and I will be in touch shortly."}</p>

                                <div className="contact-status-region" role="status" aria-live="polite">
                                    {showError && <p className="contact-form-alert contact-form-alert-error">{formError}</p>}
                                    {showSuccess && (
                                        <div className="contact-form-alert contact-form-alert-success">
                                            <p>Your enquiry has been sent successfully.</p>
                                            <button type="button" className="btn button contact-submit" onClick={() => setSubmitState("idle")}>Send Another Message</button>
                                        </div>
                                    )}
                                </div>

                                {!showSuccess && (
                                    <form method="POST" action="/contact-us" className="contact-form" onSubmit={onFormSubmit} aria-busy={submitState === "submitting"}>
                                        <div className="form-group contact-honeypot" aria-hidden="true">
                                            <label htmlFor="companyWebsiteInput">Company website</label>
                                            <input type="text" className="form-control" name="companyWebsite" id="companyWebsiteInput" tabIndex="-1" autoComplete="off" value={formState.companyWebsite} onChange={onFieldChange} />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="nameInput">Name</label>
                                            <input type="text" className="form-control" name="name" id="nameInput" placeholder="Your name" required value={formState.name} onChange={onFieldChange} />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="organisationInput">Organisation</label>
                                            <input type="text" className="form-control" name="organisation" id="organisationInput" placeholder="Organisation" value={formState.organisation} onChange={onFieldChange} />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="emailInput">Email address</label>
                                            <input type="email" className="form-control" name="email" id="emailInput" placeholder="name@firm.com" required value={formState.email} onChange={onFieldChange} />
                                        </div>
                                        <div className="form-group">
                                            <label htmlFor="messageInput">Message</label>
                                            <textarea className="form-control" id="messageInput" name="message" placeholder="Tell me how I can help your team" rows="8" required value={formState.message} onChange={onFieldChange}></textarea>
                                        </div>
                                        {recaptchaSiteKey && (
                                            <div className="form-group contact-recaptcha">
                                                <ReCAPTCHA ref={recaptchaRef} sitekey={recaptchaSiteKey} onChange={(token) => setRecaptchaToken(token || "")} />
                                            </div>
                                        )}
                                        <button className="btn button contact-submit" type="submit" disabled={submitState === "submitting"}>{submitState === "submitting" ? "Sending..." : "Send Message"}</button>
                                    </form>
                                )}
                            </div>
                        </div>

                        <div className="col-md-5">
                            <aside className="contact-details-panel" aria-label="Contact details">
                                <h3>Direct Contact</h3>
                                <p className="contact-panel-copy">If you prefer to get in touch directly, please use the details below.</p>
                                <div className="contact-detail-item">
                                    <span className="contact-detail-label">Email</span>
                                    <a href="mailto:laura@lcmillsconsulting.com" onClick={() => trackEvent("contact_direct_click", { method: "email", page_path: "/contact" })}>laura@lcmillsconsulting.com</a>
                                </div>
                                <div className="contact-detail-item">
                                    <span className="contact-detail-label">Tel (US)</span>
                                    <a href="tel:+17162758509" onClick={() => trackEvent("contact_direct_click", { method: "phone_us", page_path: "/contact" })}>+1 (716) 275-8509</a>
                                </div>
                                <div className="contact-detail-item">
                                    <span className="contact-detail-label">Tel (UK)</span>
                                    <a href="tel:+447946167360" onClick={() => trackEvent("contact_direct_click", { method: "phone_uk", page_path: "/contact" })}>+44 (0) 7946167360</a>
                                </div>
                                <p className="contact-response-note">Typical response time: within 1-2 business days.</p>
                            </aside>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
